import * as z from 'zod';
import { strict } from './common.js';
/**
 * Cursor pagination, because offset pagination over a list that is being written to skips and
 * repeats rows.
 *
 * The cursor is opaque on purpose: it encodes the sort key and a digest of the filter it was
 * issued under, so presenting it against a different filter is refused rather than quietly
 * answering the wrong question. Its contents are the server's business; this schema only pins that
 * it is a bounded, URL-safe string, so a caller cannot use it as a channel for something else.
 */
/** The default page size. Small enough to render at a gate, large enough not to thrash. */
export const DEFAULT_PAGE_SIZE = 25;
/** The largest page anybody may ask for, including an API key (AD-34's exfiltration surface). */
export const MAX_PAGE_SIZE = 100;
export const sortDirectionSchema = z.enum(['asc', 'desc']);
/** An opaque, URL-safe cursor. Bounded, because an unbounded one is a request-size problem. */
export const cursorSchema = z
    .string()
    .min(1)
    .max(512)
    .regex(/^[\w-]+$/u, { error: 'errors.validation.cursor_invalid' });
/** The query parameters every list route accepts, and nothing else. */
export const paginationQuerySchema = strict({
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    cursor: cursorSchema.optional(),
    direction: sortDirectionSchema.default('asc'),
});
/**
 * One page of results.
 *
 * `nextCursor` is present exactly when there is more to fetch, so a client loops on its presence
 * rather than on comparing a count against a page size, which is the comparison that goes wrong on
 * the last page.
 */
export function pageSchema(item) {
    return strict({
        items: z.array(item),
        nextCursor: cursorSchema.nullable(),
        totalEstimate: z.int().nonnegative().nullable(),
    });
}
/**
 * A whole list in one response, for a collection that is never paged.
 *
 * **Two envelopes rather than one, and the difference is a promise rather than a style.** A
 * {@link pageSchema} body says "there may be more, and `nextCursor` is how you ask for it"; this one
 * says "this is all of it". The collections that use it are bounded by something other than a page
 * size — the staff of one facility, the field registry, the photographs of one person, the open
 * alert set — so a cursor would be a member that is always `null` and a loop nobody ever takes a
 * second turn of. Handing a client a page envelope over a complete list is how a consumer ends up
 * writing pagination code for a list that cannot paginate, and handing it a bare array is how a
 * response grows a second member later and breaks every parser at once.
 */
export function listSchema(item) {
    return strict({ items: z.array(item) });
}
/**
 * One NUMBERED page: which page this is, how many there are, and how many matched.
 *
 * **A third envelope rather than a member on {@link pageSchema}, and the difference is again a
 * promise rather than a style.** A page envelope says "there may be more, and `nextCursor` is how
 * you ask for it"; a list envelope says "this is all of it"; this one says "this is page `page` of
 * `pageCount`, and you may ask for any of them". Only a caller that can ORDER the list itself needs
 * it: a cursor resumes after a key, and a list whose key is chosen by whoever is reading it has no
 * key to resume after. The register is the one such list in this product, and `persons/list.ts`
 * carries the argument for why it is paged this way and nothing else is.
 *
 * **`pageCount` is what is REACHABLE and `total` is what MATCHED, and they are allowed to
 * disagree.** A search engine that caps its answer has more matches than it will ever serve, so
 * `total` may exceed `pageCount * limit`; asking for a page beyond `pageCount` is answered with an
 * empty `items` rather than a refusal, because the honest reply to a stale link is the list as it is
 * now. Reporting the cap as the count is what turns "there are more" into "that is all of them".
 */
export function numberedPageSchema(item) {
    return strict({
        items: z.array(item),
        /** The page served, 1-based: `page` 1 is the first, and there is no page 0. */
        page: z.int().positive(),
        /** How many pages this query can serve. `0` when nothing matched. */
        pageCount: z.int().nonnegative(),
        /** How many rows matched everything that was asked, which a capped answer may exceed. */
        total: z.int().nonnegative(),
    });
}
/**
 * The query a CURSOR-paged route accepts: a page size and a cursor, and no direction.
 *
 * **A route declares exactly the query it reads.** `api_key.list` and `api_key.usage` both answer
 * newest-first unconditionally — the cursor is bound to that direction and `buildPage` is called
 * with it as a literal — so declaring a `direction` they never look at would tell a caller their
 * choice was understood while every row comes back in the other order. `auditQuerySchema` omits it
 * for the same reason; this is that omission given a name, so the two routes can import an
 * identifier rather than build one, which `openapi/registry.ts` requires.
 */
export const cursorPageQuerySchema = paginationQuerySchema.omit({ direction: true });
/**
 * A list the server DELIBERATELY cut, and how many there were before it did.
 *
 * **A fourth envelope, and the promise is the one the other three cannot make.** A page envelope
 * says "there may be more, and `nextCursor` is how you ask"; a list envelope says "this is all of
 * it"; a numbered page says "this is page `page` of `pageCount`". This one says **"these are the
 * most significant `items.length` of `total` rows, and there is no way to ask for the rest"** — the
 * way to see other rows is to ask a NARROWER QUESTION, which for every user of this envelope means
 * a narrower date range. The analytics module has said exactly that about its date cap since it was
 * written; this says it about rows.
 *
 * **Two members, not three.** The cap itself is not one of them: `items.length` already is it
 * whenever the list was cut, and the ceiling is published where an integrator reads a ceiling — as
 * `maxItems` in the emitted OpenAPI document. A `limit` member would restate a constant in every
 * response.
 *
 * **What makes a `.max()` here safe is that the SERVER applied it.** A ceiling on a list the server
 * caps can never be violated, because the server produced the number; a ceiling on a list it does
 * not cap is a guess about production, and the browser rejects a response that fails its schema, so
 * a wrong guess is a blank panel rather than a refused request.
 */
export function cappedListSchema(item, max) {
    return strict({
        items: z.array(item).max(max),
        /** How many rows matched before the cap. Never less than `items.length`. */
        total: z.int().nonnegative(),
    });
}
//# sourceMappingURL=pagination.js.map